import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Box, type Theme, Typography, useTheme } from '@mui/material';
import Stack from '@mui/material/Stack';
import { useMemo } from 'react';
import useUnitProvider from '/@/core/context/unit';
import { flattenGeometry, type Geometry, type Polygon } from '/@/core/fields/geometry';
import settings, { isExpertoBayer } from '/@/core/settings';
import { convertAreaFromM2 } from '/@/hooks/geometry/area';
import { isMultiPolygon, isPoint } from '/@/utils';
import Icon from '../icon';
import colors from '../theme-provider/colors';

const CANVAS_SIZE = 40;

const toSvgPoint = (mapCenterX: number, mapCenterY: number, scale: number, x: number, y: number) => {
    const px = (x - mapCenterX) * scale + CANVAS_SIZE / 2;
    const py = -(y - mapCenterY) * scale + CANVAS_SIZE / 2; // invert Y
    return `${px},${py}`;
};

const RenderedPolygon = ({
    geometry,
    fill,
    stroke,
    strokeWidth,
    mapCenterX,
    mapCenterY,
    scale,
}: {
    geometry: Polygon;
    fill: string;
    stroke: string;
    strokeWidth: number;
    mapCenterX: number;
    mapCenterY: number;
    scale: number;
}) => {
    // Normalize a ring that could be [[x,y], ...] or [x1,y1,x2,y2,...]
    const toPairs = (ring: any[] = []) => {
        if (!Array.isArray(ring) || ring.length === 0) return [];
        if (Array.isArray(ring[0])) {
            return (ring as any[])
                .filter((p) => Array.isArray(p) && p.length >= 2)
                .map(([x, y]) => [Number(x), Number(y)]);
        }
        // Flat numeric list -> chunk into pairs
        const nums = ring.filter((v) => typeof v === 'number');
        const pairs: [number, number][] = [];
        for (let i = 0; i + 1 < nums.length; i += 2) {
            pairs.push([Number(nums[i]), Number(nums[i + 1])]);
        }
        return pairs;
    };

    // Determine rings shape: either [[ring1],[ring2],...] or a flat ring [x1,y1,x2,y2,...]
    const rings: any[] = Array.isArray(geometry?.[0]) ? (geometry as any[]) : [geometry as any[]];
    const outerPairs = toPairs(rings?.[0] as any[]);
    if (outerPairs.length < 3) return null;
    const outerPolygon = outerPairs.map(([x, y]) => toSvgPoint(mapCenterX, mapCenterY, scale, x, y)).join(' ');
    const innerPolygons = rings.slice(1).map((ring) => {
        const pairs = toPairs(ring as any[]);
        return pairs.map(([x, y]) => toSvgPoint(mapCenterX, mapCenterY, scale, x, y)).join(' ');
    });
    return (
        <>
            <polygon
                points={outerPolygon}
                fill={fill}
                stroke={stroke}
                strokeWidth={strokeWidth}
                strokeLinejoin='round'
                strokeLinecap='round'
                fillRule='evenodd'
            />

            {innerPolygons.map((p, i) => (
                <polygon
                    key={i}
                    points={p}
                    fill={'white'}
                    stroke={stroke}
                    strokeWidth={strokeWidth}
                    strokeLinejoin='round'
                    strokeLinecap='round'
                    fillRule='evenodd'
                />
            ))}
        </>
    );
};

export const FieldIcon = ({ coordinates, selected = false }: { coordinates: Geometry; selected: boolean }) => {
    const theme = useTheme();

    // Flatten geometry to get all coordinates
    const flattenedCoordinates = useMemo(() => flattenGeometry(coordinates), [coordinates]);

    // Configuration
    const { minX, minY, maxX, maxY } = useMemo(() => {
        const XValues = flattenedCoordinates.map((c) => c[0]);
        const YValues = flattenedCoordinates.map((c) => c[1]);
        return {
            minX: Math.min(...XValues),
            minY: Math.min(...YValues),
            maxX: Math.max(...XValues),
            maxY: Math.max(...YValues),
        };
    }, [flattenedCoordinates]);

    const mapWidth = maxX - minX;
    const mapHeight = maxY - minY;
    const mapCenterX = (maxX + minX) / 2;
    const mapCenterY = (maxY + minY) / 2;
    const scale = Math.min(mapWidth ? CANVAS_SIZE / mapWidth : 1, mapHeight ? CANVAS_SIZE / mapHeight : 1) * 0.9;

    const stroke = colors.themes?.[settings.app.theme]?.primary?.[600];
    const fill = (theme.palette.primary as any).infoBackground;
    const strokeWidth = selected ? 2 : 1;

    if (isPoint(coordinates))
        return (
            <LocationOnIcon
                fontSize='large'
                sx={[
                    {
                        width: '40px',
                        height: '40px',
                        color: (t: Theme) => colors.themes?.[settings.app.theme]?.primary?.[600],
                    },
                ]}
            />
        );

    return (
        <svg
            width={CANVAS_SIZE}
            height={CANVAS_SIZE}
            viewBox={`0 0 ${CANVAS_SIZE} ${CANVAS_SIZE}`}
            xmlns='http://www.w3.org/2000/svg'
            style={{ overflow: 'clip' }}
        >
            <title>Field icon</title>
            {isMultiPolygon(coordinates) ? (
                coordinates.map((polygon, idx) => (
                    <RenderedPolygon
                        key={idx}
                        geometry={polygon}
                        fill={fill}
                        stroke={stroke}
                        strokeWidth={strokeWidth}
                        mapCenterX={mapCenterX}
                        mapCenterY={mapCenterY}
                        scale={scale}
                    />
                ))
            ) : (
                <RenderedPolygon
                    geometry={coordinates}
                    fill={fill}
                    stroke={stroke}
                    strokeWidth={strokeWidth}
                    mapCenterX={mapCenterX}
                    mapCenterY={mapCenterY}
                    scale={scale}
                />
            )}
        </svg>
    );
};

const FieldCard = ({
    name,
    geometry,
    area,
    location,
    altitude,
    selected = false,
    unitProviderProps,
    isFieldview,
}: {
    name: string;
    geometry: Geometry;
    area?: number;
    location?: string;
    altitude?: number;
    selected?: boolean;
    unitProviderProps?: any;
    isFieldview?: boolean;
}) => {
    const units = useUnitProvider(unitProviderProps);
    return (
        <Stack direction='row' spacing={2} sx={{ alignItems: 'center', justifyContent: 'start' }}>
            <FieldIcon coordinates={geometry} selected={selected} />
            <Stack
                direction='column'
                spacing={0.5}
                sx={{
                    alignItems: 'start',
                    textAlign: 'left',
                }}
            >
                <Stack direction='row' spacing={1} alignItems='center'>
                    <Typography
                        className='mp-mask'
                        variant={isExpertoBayer() ? 'h6' : 'body2'}
                        color='primary'
                        sx={{
                            wordBreak: 'break-word',
                        }}
                    >
                        {name}
                    </Typography>
                    {isFieldview && (
                        <Box>
                            <Icon type='fieldview' style={{ fill: '#F7B618', width: '20px', height: '20px' }} />
                        </Box>
                    )}
                </Stack>
                <Typography
                    className='mp-mask'
                    variant={isExpertoBayer() ? 'body2' : 'caption'}
                    color={'primary.mui.inactive'}
                >
                    {area
                        ? [convertAreaFromM2(area, units).toString(), location].filter(Boolean).join(' - ')
                        : location}
                </Typography>
            </Stack>
        </Stack>
    );
};

export default FieldCard;
